/**
 * Guardian API service layer.
 * All calls go through the existing `api` Axios instance which adds JWT + org headers automatically.
 * Route: /api/guardian/* → API gateway → Guardian service
 */

import { api } from './api'

// ── Types ────────────────────────────────────────────────────

export interface GuardianApp {
  id: number
  organization_id: number
  app_name: string
  app_slug: string
  platform: string
  framework: string
  repo_url: string
  git_provider: string
  repo_branch: string
  is_active: boolean
  scan_frequency: string | null
  auto_merge_enabled: boolean | null
  last_scan_at: string | null
  dep_count: number
  findings_high: number
  findings_medium: number
  findings_low: number
  findings_open: number
  findings_in_pr: number
  last_action_at: string | null
  last_action_type: string | null
  last_action_actor: string | null
  created_at: string
  updated_at: string | null
}

export interface GuardianDependency {
  id: number
  app_id: number
  package_manager: string
  package_name: string
  current_version: string
  latest_version: string | null
  source_file: string
  is_dev_dep: boolean
  is_direct: boolean
}

export interface GuardianFinding {
  id: number
  app_id: number
  finding_type: string
  severity: string
  title: string
  description: string | null
  recommendation: string | null
  cve_id: string | null
  finding_status: string
  fix_pr_url: string | null
  finding_metadata: Record<string, unknown> | null
  created_at: string
}

export interface GuardianScan {
  id: number
  app_id: number
  scan_type: string
  scan_status: string
  summary: Record<string, unknown> | null
  created_at: string
}

export interface GuardianQARun {
  id: number
  app_id: number
  finding_id: number | null
  trigger_type: string
  run_status: string
  overall_verdict: string | null
  confidence: number | null
  created_at: string
}

export interface GuardianDeployKey {
  id: number
  provider: string
  label: string
  public_key: string
  is_active: boolean
  created_at: string
  last_used_at: string | null
}

export interface GuardianPortfolioHealth {
  total_apps: number
  active_apps: number
  total_findings: number
  critical: number
  high: number
  medium: number
  low: number
  health_score: number | null
  app_scores: Record<string, number>
}

export interface GuardianSettings {
  scan_frequency: string
  scan_time_utc: string
  auto_merge_enabled: boolean
  auto_merge_policy: string
  notifications_enabled: boolean
}

export interface TestAccessResult {
  success: boolean
  message: string
  files_found: number
  detected_framework?: string
}

// ── API Methods ──────────────────────────────────────────────

export const guardianService = {
  // Portfolio
  getPortfolioHealth: () =>
    api.get<GuardianPortfolioHealth>('/guardian/portfolio/health').then(r => r.data),

  // Apps
  getApps: () =>
    api.get<GuardianApp[]>('/guardian/apps').then(r => r.data),
  getApp: (slug: string) =>
    api.get<GuardianApp>(`/guardian/apps/${slug}`).then(r => r.data),
  testAccess: (data: { repo_url: string; git_provider: string; repo_branch: string }) =>
    api.post<TestAccessResult>('/guardian/apps/test-access', data).then(r => r.data),
  registerApp: (data: {
    app_name: string; app_slug: string; repo_url: string;
    git_provider?: string; framework?: string; repo_branch?: string
  }) =>
    api.post('/guardian/apps', data).then(r => r.data),
  deactivateApp: (slug: string) =>
    api.delete(`/guardian/apps/${slug}`),

  // Dependencies & Findings
  getAppDependencies: (slug: string) =>
    api.get<GuardianDependency[]>(`/guardian/apps/${slug}/dependencies`).then(r => r.data),
  getAppFindings: (slug: string) =>
    api.get<GuardianFinding[]>(`/guardian/apps/${slug}/findings`).then(r => r.data),
  getFindings: () =>
    api.get<GuardianFinding[]>('/guardian/findings').then(r => r.data),

  // Scans
  getScans: () =>
    api.get<GuardianScan[]>('/guardian/scans').then(r => r.data),
  triggerScan: () =>
    api.post('/guardian/scans/trigger').then(r => r.data),
  triggerAppScan: (slug: string) =>
    api.post(`/guardian/scans/trigger/${slug}`).then(r => r.data),
  triggerScanSelected: (slugs: string[]) =>
    api.post(`/guardian/scans/trigger/selected?slugs=${slugs.join(',')}`).then(r => r.data),

  // QA
  getQARuns: (slug: string) =>
    api.get<GuardianQARun[]>(`/guardian/qa/runs/${slug}`).then(r => r.data),
  triggerQA: (slug: string) =>
    api.post(`/guardian/qa/run/${slug}`).then(r => r.data),
  triggerQAFinding: (slug: string, findingId: number) =>
    api.post(`/guardian/qa/run/${slug}/finding/${findingId}`).then(r => r.data),
  triggerBundleQA: (slug: string, findingIds: number[]) =>
    api.post(`/guardian/qa/bundle/${slug}`, { finding_ids: findingIds }).then(r => r.data),
  getQAChecks: (runId: number) =>
    api.get(`/guardian/qa/run/${runId}/checks`).then(r => r.data),

  // Actions
  createPR: (slug: string, findingId: number) =>
    api.post(`/guardian/actions/create-pr/${slug}/finding/${findingId}`).then(r => r.data),
  createBundledPR: (slug: string, findingIds: number[], qaRunId: number) =>
    api.post(`/guardian/actions/create-bundled-pr/${slug}`, { finding_ids: findingIds, qa_run_id: qaRunId }).then(r => r.data),
  getAppActions: (slug: string) =>
    api.get(`/guardian/actions/app/${slug}`).then(r => r.data),
  updateAppSettings: (slug: string, data: { scan_frequency?: string; auto_merge_enabled?: boolean }) =>
    api.put(`/guardian/apps/${slug}/settings`, data).then(r => r.data),
  getAuditLog: (limit?: number) =>
    api.get(`/guardian/audit`, { params: { limit } }).then(r => r.data),
  getAuditDetail: (entryId: number) =>
    api.get(`/guardian/audit/${entryId}/detail`).then(r => r.data),

  // Deploy Keys
  getDeployKeys: () =>
    api.get<GuardianDeployKey[]>('/guardian/settings/deploy-keys').then(r => r.data),
  generateDeployKey: (provider: string) =>
    api.post('/guardian/settings/deploy-keys', { provider }).then(r => r.data),
  revokeDeployKey: (keyId: number) =>
    api.delete(`/guardian/settings/deploy-keys/${keyId}`),

  // Settings
  getSettings: () =>
    api.get<GuardianSettings>('/guardian/settings').then(r => r.data),
  updateSettings: (data: Partial<GuardianSettings>) =>
    api.put('/guardian/settings', data).then(r => r.data),

  // Timeline
  getTimeline: (slug: string) =>
    api.get(`/guardian/apps/${slug}/timeline`).then(r => r.data),

  // OAuth
  getOAuthUrl: (provider: string) =>
    api.get<{ oauth_url: string; state: string }>(`/guardian/oauth/authorize/${provider}`).then(r => r.data),
  oauthCallback: (code: string, state: string) =>
    api.post('/guardian/oauth/callback', { code, state }).then(r => r.data),
  getConnectionStatus: () =>
    api.get<Record<string, { connected: boolean }>>('/guardian/oauth/status').then(r => r.data),
  getUserInfo: (provider: string) =>
    api.get<GuardianOAuthUserInfo>(`/guardian/oauth/user-info/${provider}`).then(r => r.data),
  getRepos: (provider: string) =>
    api.get<GuardianRepo[]>(`/guardian/oauth/repos/${provider}`).then(r => r.data),
  disconnectProvider: (provider: string) =>
    api.delete(`/guardian/oauth/disconnect/${provider}`),
  connectRepo: (repoUrl: string) =>
    api.post('/guardian/apps/connect', { repo_url: repoUrl }).then(r => r.data),
}

export interface GuardianRepo {
  full_name: string
  name: string
  slug: string
  description: string
  language: string
  ssh_url: string
  https_url: string
  default_branch: string
  updated_on: string
  size: number
  workspace: string
  project: string
}

export interface GuardianOAuthUserInfo {
  authenticated: boolean
  display_name?: string
  username?: string
  account_id?: string
  avatar_url?: string
  workspaces?: Array<{ slug: string; name: string }>
}
